/**
 * Copyright (c) 2026 Accunai India Services Pvt. Ltd. All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This file is part of proprietary software owned by Accunai India Services Pvt. Ltd.
 * Unauthorized copying, distribution, modification, or use of this file,
 * in whole or in part, via any medium, is strictly prohibited without
 * prior written permission from Accunai India Services Pvt. Ltd.
 *
 * Generated with GitHub Copilot Business/Enterprise.
 * Reviewed and approved by: AI-assisted / 2026-08-13
 */

import {
  fetchProjectTypesApi,
  fetchTaskCategoriesApi,
  getProjectsApi,
  updateProjectApi,
} from "./projectApi";
import {
  OptionsApiResponse,
  ProjectFormDependencies,
  ProjectFormDependenciesInput,
  ProjectListQuery,
  ProjectListResult,
  ProjectOption,
  ProjectOptionRecord,
  ProjectRecord,
  UpdateProjectInput,
} from "../features/projects/model/types";

const mapProjectOption = (item: ProjectOptionRecord): ProjectOption => ({
  id: item.id,
  name: item.name,
  value: item.value,
});

const normalizeOptionsResponse = (response: OptionsApiResponse): ProjectOption[] => {
  if (Array.isArray(response)) {
    return response.map(mapProjectOption);
  }

  return (response.results ?? []).map(mapProjectOption);
};

export const getProjects = async (query: ProjectListQuery = {}): Promise<ProjectListResult> => {
  const response = await getProjectsApi(query);

  if (Array.isArray(response.results)) {
    return {
      count: response.count ?? response.results.length,
      results: response.results,
    };
  }

  const directResult = response as unknown as ProjectRecord;
  if (directResult?.id) {
    return {
      count: 1,
      results: [directResult],
    };
  }

  return {
    count: 0,
    results: [],
  };
};

export const updateProject = async (payload: UpdateProjectInput): Promise<ProjectRecord | null> => {
  const response = await updateProjectApi(payload);

  if (response.data) {
    return response.data;
  }

  return null;
};

export const getProjectFormDependencies = async (
  input: ProjectFormDependenciesInput
): Promise<ProjectFormDependencies> => {
  const errors: string[] = [];

  const [projectTypesResult, taskCategoriesResult] = await Promise.allSettled([
    fetchProjectTypesApi({ dataCategory: input.dataCategory, paginate: false }),
    fetchTaskCategoriesApi({ annotationMethod: input.annotationMethod, paginate: false }),
  ]);

  const projectTypes =
    projectTypesResult.status === "fulfilled"
      ? normalizeOptionsResponse(projectTypesResult.value)
      : [];

  if (projectTypesResult.status === "rejected") {
    errors.push("Failed to load project types.");
  }

  const taskCategories =
    taskCategoriesResult.status === "fulfilled"
      ? normalizeOptionsResponse(taskCategoriesResult.value)
      : [];

  if (taskCategoriesResult.status === "rejected") {
    errors.push("Failed to load task categories.");
  }

  return {
    projectTypes,
    taskCategories,
    errors,
  };
};
